import { useState, useRef, useEffect, useCallback } from 'react'
import type { HTMLAttributes } from 'react'
import { createPortal } from 'react-dom'
import { useTheme } from '../theme'
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  CalendarDays,
} from 'lucide-react'
import styles from './DateTimePicker.module.css'

export interface DateTimePickerProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value?: string            // ISO string or datetime-local format
  /**
   * Called with local wall-clock time and NO timezone — "2026-12-05T14:00" —
   * the same shape as a native datetime-local input. That is not RFC 3339:
   * convert it with `new Date(value).toISOString()` before sending it to the
   * API, which otherwise refuses it or reads it as UTC.
   */
  onChange?: (value: string) => void
  mode?: 'datetime' | 'date' | 'time'
  /** Format string for the displayed value. Tokens: YYYY, YY, MMMM, MMM, MM, M, DD, D, dddd, ddd, hh, h, HH, H, mm, ss, A, a */
  displayFormat?: string
  placeholder?: string
  error?: boolean
  disabled?: boolean
  initialViewDate?: string  // ISO string — calendar opens to this month when no value is set
  min?: string
  max?: string
}

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

type CalView = 'days' | 'months' | 'years'

function padTwo(n: number) {
  return n.toString().padStart(2, '0')
}

function toLocalDatetime(date: Date): string {
  return `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(date.getDate())}T${padTwo(date.getHours())}:${padTwo(date.getMinutes())}`
}

const DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS_FULL = MONTHS

function formatWithTokens(d: Date, fmt: string): string {
  const h24 = d.getHours()
  const h12 = h24 % 12 || 12
  // Ordered longest-first so "MMMM" matches before "MMM" before "MM" before "M"
  const tokens: [string, string][] = [
    ['YYYY', String(d.getFullYear())],
    ['YY', String(d.getFullYear()).slice(-2)],
    ['MMMM', MONTHS_FULL[d.getMonth()]],
    ['MMM', MONTHS_SHORT[d.getMonth()]],
    ['MM', padTwo(d.getMonth() + 1)],
    ['M', String(d.getMonth() + 1)],
    ['dddd', DAYS_FULL[d.getDay()]],
    ['ddd', DAYS_SHORT[d.getDay()]],
    ['DD', padTwo(d.getDate())],
    ['D', String(d.getDate())],
    ['hh', padTwo(h12)],
    ['h', String(h12)],
    ['HH', padTwo(h24)],
    ['H', String(h24)],
    ['mm', padTwo(d.getMinutes())],
    ['ss', padTwo(d.getSeconds())],
    ['A', h24 >= 12 ? 'PM' : 'AM'],
    ['a', h24 >= 12 ? 'pm' : 'am'],
  ]
  // Replace tokens with unique placeholders first, then swap placeholders for values.
  // This prevents replaced values (e.g. "March") from being matched by later tokens ("M", "a", "h").
  let result = fmt
  const placeholders: string[] = []
  for (let i = 0; i < tokens.length; i++) {
    const [token] = tokens[i]
    const ph = `\x00${i}\x00`
    placeholders.push(ph)
    result = result.split(token).join(ph)
  }
  for (let i = 0; i < tokens.length; i++) {
    result = result.split(placeholders[i]).join(tokens[i][1])
  }
  return result
}

function formatDisplay(value: string, mode: 'datetime' | 'date' | 'time', displayFormat?: string): string {
  /* v8 ignore next -- unreachable defensive guard */
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d.getTime())) return value

  if (displayFormat) return formatWithTokens(d, displayFormat)

  if (mode === 'time') {
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  }
  if (mode === 'date') {
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
  }
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) +
    ' at ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay()
}

function parseManualInput(input: string, mode: 'datetime' | 'date' | 'time'): Date | null {
  /* v8 ignore next -- unreachable defensive guard */
  if (!input.trim()) return null

  // Try native Date parsing first (handles ISO strings, "Mar 23, 2026", etc.)
  const direct = new Date(input)
  if (!isNaN(direct.getTime())) return direct

  // Try common manual formats: "3/23/2026", "03/23/2026", "3-23-2026"
  const datePattern = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/
  const dateTimePattern = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})\s+(\d{1,2}):(\d{2})\s*(am|pm)?$/i

  if (mode === 'time') {
    const timeMatch = input.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i)
    if (timeMatch) {
      let h = parseInt(timeMatch[1])
      const m = parseInt(timeMatch[2])
      const ap = timeMatch[3]?.toUpperCase()
      if (ap === 'PM' && h < 12) h += 12
      if (ap === 'AM' && h === 12) h = 0
      const d = new Date()
      d.setHours(h, m, 0, 0)
      return d
    }
    return null
  }

  const dtMatch = input.match(dateTimePattern)
  if (dtMatch) {
    const [, mo, day, yr, hr, min, ap] = dtMatch
    let year = parseInt(yr)
    if (year < 100) year += 2000
    let hour = parseInt(hr)
    if (ap?.toUpperCase() === 'PM' && hour < 12) hour += 12
    if (ap?.toUpperCase() === 'AM' && hour === 12) hour = 0
    const d = new Date(year, parseInt(mo) - 1, parseInt(day), hour, parseInt(min))
    /* v8 ignore next -- invalid time components never produce NaN here (regex only matches well-formed time) */
    return isNaN(d.getTime()) ? null : d
  }

  const dMatch = input.match(datePattern)
  if (dMatch) {
    const [, mo, day, yr] = dMatch
    let year = parseInt(yr)
    if (year < 100) year += 2000
    const d = new Date(year, parseInt(mo) - 1, parseInt(day))
    /* v8 ignore next -- invalid date components roll over rather than NaN (regex only matches well-formed dates) */
    return isNaN(d.getTime()) ? null : d
  }

  return null
}

export function DateTimePicker({
  value,
  onChange,
  mode = 'datetime',
  displayFormat,
  placeholder,
  error,
  disabled,
  initialViewDate,
  min,
  max,
  className,
  ...props
}: DateTimePickerProps) {
  const { theme } = useTheme()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [inputValue, setInputValue] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [calView, setCalView] = useState<CalView>('days')
  const [yearRangeStart, setYearRangeStart] = useState(Math.floor((new Date().getFullYear()) / 12) * 12)

  // Calendar state
  const now = new Date()
  const parsed = value ? new Date(value) : null
  const fallback = initialViewDate ? new Date(initialViewDate) : now
  const [viewYear, setViewYear] = useState(parsed?.getFullYear() ?? fallback.getFullYear())
  const [viewMonth, setViewMonth] = useState(parsed?.getMonth() ?? fallback.getMonth())
  const [selectedDate, setSelectedDate] = useState<Date | null>(parsed)
  const [hours, setHours] = useState(parsed ? parsed.getHours() % 12 || 12 : 12)
  const [minutes, setMinutes] = useState(parsed ? parsed.getMinutes() : 0)
  const [ampm, setAmpm] = useState<'AM' | 'PM'>(parsed ? (parsed.getHours() >= 12 ? 'PM' : 'AM') : 'PM')

  // Menu positioning
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 })

  const updatePosition = useCallback(() => {
    /* v8 ignore next -- ref is always attached while the component is mounted */
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const margin = 8
    const vw = window.innerWidth
    const vh = window.innerHeight

    // Measure the menu once it's mounted; fall back to estimates on first pass.
    const menuRect = menuRef.current?.getBoundingClientRect()
    const menuWidth = menuRect?.width || 300
    const menuHeight = menuRect?.height || 400

    const spaceBelow = vh - rect.bottom
    const spaceAbove = rect.top
    // Flip above only when there isn't room below and there's more room above.
    const flipped = spaceBelow < menuHeight + margin && spaceAbove > spaceBelow

    // Vertical: anchor to the trigger, then clamp fully on-screen.
    let top = flipped ? rect.top - 4 - menuHeight : rect.bottom + 4
    top = Math.min(top, vh - menuHeight - margin)
    top = Math.max(margin, top)

    // Horizontal: align to the trigger's left edge, then clamp on-screen so the
    // month-navigation controls are never pushed off the viewport.
    let left = Math.min(rect.left, vw - menuWidth - margin)
    left = Math.max(margin, left)

    setMenuPos({ top, left })
  }, [])

  useEffect(() => {
    if (!open) return
    updatePosition()
    // Re-measure on the next frame now that the menu is in the DOM and we can
    // read its real dimensions for accurate clamping.
    const raf = requestAnimationFrame(updatePosition)

    function handleClickOutside(e: MouseEvent) {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        menuRef.current?.contains(e.target as Node)
      ) return
      setOpen(false)
    }

    function handleScroll() { updatePosition() }

    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleScroll)
    return () => {
      cancelAnimationFrame(raf)
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleScroll)
    }
  }, [open, updatePosition])

  // Sync internal state when value prop changes
  useEffect(() => {
    if (!value) {
      if (!isEditing) setInputValue('')
      return
    }
    const d = new Date(value)
    if (isNaN(d.getTime())) return
    setSelectedDate(d)
    setViewYear(d.getFullYear())
    setViewMonth(d.getMonth())
    setHours(d.getHours() % 12 || 12)
    setMinutes(d.getMinutes())
    setAmpm(d.getHours() >= 12 ? 'PM' : 'AM')
    if (!isEditing) setInputValue(formatDisplay(value, mode, displayFormat))
  }, [value, mode, isEditing, displayFormat])

  const handleInputFocus = () => {
    setIsEditing(true)
    // Focusing/tapping the field opens the picker too (not just the icon button).
    if (!open) {
      setCalView('days')
      setOpen(true)
      updatePosition()
    }
    // Select all text on focus for easy replacement
    setTimeout(() => inputRef.current?.select(), 0)
  }

  const commitInput = () => {
    setIsEditing(false)
    if (!inputValue.trim()) {
      if (value) setInputValue(formatDisplay(value, mode, displayFormat))
      return
    }
    const d = parseManualInput(inputValue, mode)
    if (d) {
      onChange?.(toLocalDatetime(d))
    } else {
      // Revert to current value on invalid input
      setInputValue(value ? formatDisplay(value, mode, displayFormat) : '')
    }
  }

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitInput()
      inputRef.current?.blur()
    }
    if (e.key === 'Escape') {
      setIsEditing(false)
      /* v8 ignore next -- escape-revert with no value set; the value branch is exercised elsewhere */
      setInputValue(value ? formatDisplay(value, mode, displayFormat) : '')
      inputRef.current?.blur()
    }
  }

  const emitChange = useCallback((date: Date, h: number, m: number, ap: 'AM' | 'PM') => {
    let hour24 = h % 12
    if (ap === 'PM') hour24 += 12
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour24, m)
    onChange?.(toLocalDatetime(d))
  }, [onChange])

  const handleDayClick = (d: Date) => {
    setSelectedDate(d)
    // Navigate the view to the clicked date's month (handles adjacent-month days)
    setViewYear(d.getFullYear())
    setViewMonth(d.getMonth())

    if (mode === 'date') {
      onChange?.(toLocalDatetime(d))
      setOpen(false)
    } else {
      emitChange(d, hours, minutes, ampm)
    }
  }

  const handleTimeChange = (h: number, m: number, ap: 'AM' | 'PM') => {
    setHours(h)
    setMinutes(m)
    setAmpm(ap)
    if (selectedDate) {
      emitChange(selectedDate, h, m, ap)
    }
  }

  // Parse min/max into start-of-day Date objects for day-level comparison
  const minDate = min ? new Date(new Date(min).getFullYear(), new Date(min).getMonth(), new Date(min).getDate()) : null
  const maxDate = max ? new Date(new Date(max).getFullYear(), new Date(max).getMonth(), new Date(max).getDate()) : null

  const canGoPrev = !minDate || new Date(viewYear, viewMonth, 0) >= minDate
  const canGoNext = !maxDate || new Date(viewYear, viewMonth + 1, 1) <= maxDate

  const prevMonth = () => {
    /* v8 ignore next -- unreachable defensive guard */
    if (!canGoPrev) return
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1) }
    else setViewMonth(m => m - 1)
  }

  const nextMonth = () => {
    /* v8 ignore next -- unreachable defensive guard */
    if (!canGoNext) return
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1) }
    else setViewMonth(m => m + 1)
  }

  const firstDay = getFirstDayOfMonth(viewYear, viewMonth)
  const today = new Date()

  // Always render a fixed 6-row (42-cell) grid so the calendar height never
  // changes between months — keeps the nav arrows from shifting under the cursor.
  // Cell c maps to date (1 - firstDay + c) of the view month, which JS normalizes
  // into trailing days of the previous month and leading days of the next month.
  const calendarCells = Array.from({ length: 42 }, (_, c) =>
    new Date(viewYear, viewMonth, 1 - firstDay + c),
  )

  const defaultPlaceholder = mode === 'time' ? 'Select time' : mode === 'date' ? 'Select date' : 'Select date & time'
  const displayValue = value ? formatDisplay(value, mode, displayFormat) : ''

  const classes = [
    styles.trigger,
    open && styles.triggerOpen,
    error && styles.error,
    disabled && styles.disabled,
    className,
  ].filter(Boolean).join(' ')

  const showCalendar = mode !== 'time'
  const showTime = mode !== 'date'

  return (
    <div {...props}>
      <div ref={triggerRef} className={classes}>
        <input
          ref={inputRef}
          type="text"
          className={styles.triggerInput}
          value={isEditing ? inputValue : (displayValue || '')}
          placeholder={placeholder || defaultPlaceholder}
          disabled={disabled}
          onChange={e => setInputValue(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={commitInput}
          onKeyDown={handleInputKeyDown}
        />
        <button
          type="button"
          className={styles.triggerIconBtn}
          // No disabled check needed: a disabled button never fires onClick
          onClick={() => { if (!open) setCalView('days'); setOpen(!open); updatePosition() }}
          disabled={disabled}
          tabIndex={-1}
        >
          {mode === 'time' ? <Clock size={16} /> : <CalendarDays size={16} />}
        </button>
      </div>

      {open && createPortal(
        <div
          ref={menuRef}
          className={styles.menu}
          style={{ top: menuPos.top, left: menuPos.left }}
          data-theme={theme}
        >
          {/* Calendar */}
          {showCalendar && (
            <div className={styles.calendar}>
              {/* ── Days view ── */}
              {calView === 'days' && (
                <>
                  <div className={styles.calHeader}>
                    <button type="button" className={styles.navBtn} onClick={prevMonth} disabled={!canGoPrev} style={!canGoPrev ? { opacity: 0.25, cursor: 'not-allowed' } : undefined}>
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      type="button"
                      className={styles.monthLabelBtn}
                      onClick={() => { setCalView('months'); setYearRangeStart(Math.floor(viewYear / 12) * 12) }}
                    >
                      {MONTHS[viewMonth]} {viewYear}
                    </button>
                    <button type="button" className={styles.navBtn} onClick={nextMonth} disabled={!canGoNext} style={!canGoNext ? { opacity: 0.25, cursor: 'not-allowed' } : undefined}>
                      <ChevronRight size={16} />
                    </button>
                  </div>

                  <div className={styles.dayNames}>
                    {DAYS.map(d => <span key={d} className={styles.dayName}>{d}</span>)}
                  </div>

                  <div className={styles.dayGrid}>
                    {calendarCells.map((cellDate) => {
                      const day = cellDate.getDate()
                      const isOutside = cellDate.getMonth() !== viewMonth
                      const isToday = day === today.getDate() && cellDate.getMonth() === today.getMonth() && cellDate.getFullYear() === today.getFullYear()
                      const isSelected = selectedDate && day === selectedDate.getDate() && cellDate.getMonth() === selectedDate.getMonth() && cellDate.getFullYear() === selectedDate.getFullYear()
                      const isDisabled = (minDate && cellDate < minDate) || (maxDate && cellDate > maxDate)

                      return (
                        <button
                          key={cellDate.getTime()}
                          type="button"
                          className={[
                            styles.dayBtn,
                            isOutside && styles.dayOutside,
                            isToday && styles.dayToday,
                            isSelected && styles.daySelected,
                            isDisabled && styles.dayDisabled,
                          ].filter(Boolean).join(' ')}
                          onClick={() => !isDisabled && handleDayClick(cellDate)}
                          disabled={!!isDisabled}
                        >
                          {day}
                        </button>
                      )
                    })}
                  </div>
                </>
              )}

              {/* ── Months view ── */}
              {calView === 'months' && (() => {
                const canPrevYear = !minDate || new Date(viewYear - 1, 11, 31) >= minDate
                const canNextYear = !maxDate || new Date(viewYear + 1, 0, 1) <= maxDate
                return (
                  <>
                    <div className={styles.calHeader}>
                      <button type="button" className={styles.navBtn} onClick={() => setViewYear(y => y - 1)} disabled={!canPrevYear} style={!canPrevYear ? { opacity: 0.25, cursor: 'not-allowed' } : undefined}>
                        <ChevronLeft size={16} />
                      </button>
                      <button
                        type="button"
                        className={styles.monthLabelBtn}
                        onClick={() => { setCalView('years'); setYearRangeStart(Math.floor(viewYear / 12) * 12) }}
                      >
                        {viewYear}
                      </button>
                      <button type="button" className={styles.navBtn} onClick={() => setViewYear(y => y + 1)} disabled={!canNextYear} style={!canNextYear ? { opacity: 0.25, cursor: 'not-allowed' } : undefined}>
                        <ChevronRight size={16} />
                      </button>
                    </div>

                    <div className={styles.pickerGrid}>
                      {MONTHS_SHORT.map((m, i) => {
                        const isCurrent = i === today.getMonth() && viewYear === today.getFullYear()
                        const isSelected = i === viewMonth && viewYear === (selectedDate?.getFullYear() ?? -1)
                        const lastDayOfMonth = new Date(viewYear, i + 1, 0)
                        const firstDayOfMonth = new Date(viewYear, i, 1)
                        const isDisabled = (minDate && lastDayOfMonth < minDate) || (maxDate && firstDayOfMonth > maxDate)
                        return (
                          <button
                            key={m}
                            type="button"
                            className={[
                              styles.pickerCell,
                              isCurrent && styles.pickerCellCurrent,
                              isSelected && styles.pickerCellSelected,
                              isDisabled && styles.pickerCellDisabled,
                            ].filter(Boolean).join(' ')}
                            onClick={() => { setViewMonth(i); setCalView('days') }}
                            disabled={!!isDisabled}
                          >
                            {m}
                          </button>
                        )
                      })}
                    </div>
                  </>
                )
              })()}

              {/* ── Years view ── */}
              {calView === 'years' && (() => {
                const canPrevRange = !minDate || new Date(yearRangeStart - 1, 11, 31) >= minDate
                const canNextRange = !maxDate || new Date(yearRangeStart + 12, 0, 1) <= maxDate
                return (
                  <>
                    <div className={styles.calHeader}>
                      <button type="button" className={styles.navBtn} onClick={() => setYearRangeStart(y => y - 12)} disabled={!canPrevRange} style={!canPrevRange ? { opacity: 0.25, cursor: 'not-allowed' } : undefined}>
                        <ChevronLeft size={16} />
                      </button>
                      <span className={styles.monthLabel}>
                        {yearRangeStart} – {yearRangeStart + 11}
                      </span>
                      <button type="button" className={styles.navBtn} onClick={() => setYearRangeStart(y => y + 12)} disabled={!canNextRange} style={!canNextRange ? { opacity: 0.25, cursor: 'not-allowed' } : undefined}>
                        <ChevronRight size={16} />
                      </button>
                    </div>

                    <div className={styles.pickerGrid}>
                      {Array.from({ length: 12 }, (_, i) => {
                        const yr = yearRangeStart + i
                        const isCurrent = yr === today.getFullYear()
                        const isSelected = yr === viewYear
                        const isDisabled = (minDate && new Date(yr, 11, 31) < minDate) || (maxDate && new Date(yr, 0, 1) > maxDate)
                        return (
                          <button
                            key={yr}
                            type="button"
                            className={[
                              styles.pickerCell,
                              isCurrent && styles.pickerCellCurrent,
                              isSelected && styles.pickerCellSelected,
                              isDisabled && styles.pickerCellDisabled,
                            ].filter(Boolean).join(' ')}
                            onClick={() => { setViewYear(yr); setCalView('months') }}
                            disabled={!!isDisabled}
                          >
                            {yr}
                          </button>
                        )
                      })}
                    </div>
                  </>
                )
              })()}
            </div>
          )}

          {/* Time picker */}
          {showTime && (
            <div className={styles.timePicker}>
              <Clock size={14} className={styles.timeIcon} />
              <select
                className={styles.timeSelect}
                value={hours}
                onChange={e => handleTimeChange(Number(e.target.value), minutes, ampm)}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
              <span className={styles.timeSep}>:</span>
              <select
                className={styles.timeSelect}
                value={minutes}
                onChange={e => handleTimeChange(hours, Number(e.target.value), ampm)}
              >
                {Array.from({ length: 12 }, (_, i) => i * 5).map(m => (
                  <option key={m} value={m}>{padTwo(m)}</option>
                ))}
              </select>
              <div className={styles.ampmToggle}>
                <button
                  type="button"
                  className={`${styles.ampmBtn} ${ampm === 'AM' ? styles.ampmActive : ''}`}
                  onClick={() => handleTimeChange(hours, minutes, 'AM')}
                >AM</button>
                <button
                  type="button"
                  className={`${styles.ampmBtn} ${ampm === 'PM' ? styles.ampmActive : ''}`}
                  onClick={() => handleTimeChange(hours, minutes, 'PM')}
                >PM</button>
              </div>
            </div>
          )}

          {/* Done button */}
          {mode === 'datetime' && (
            <div className={styles.menuFooter}>
              <button type="button" className={styles.doneBtn} onClick={() => setOpen(false)}>
                Done
              </button>
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  )
}
